import React, { useState } from 'react';
import SubmitButton from '../../../../Components/Button/SubmitButton';
import * as yup from 'yup';
import FormWrapper from '../../../../Components/Form/FormWrapper';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import InputField from '../../../../Components/Form/components/InputField';
import useTranslation from '../../../../Hooks/useTranslation';
import ECardFolderSelectField from '../../../Base/Components/Fields/ECardFolder/ECardFolderSelectField';
import FormMultiplier from '../../../../Components/Form/FormMultiplier';
import { FileToShare } from '../ECard/ECard';
import FormContainer from '../../../../Components/Form/FormContainer';
// @ts-ignore
import styles from './ECardMoveFileToHealthDocumentation.module.scss';
import {
	DocumentTransferInput,
	useTransferDocumentsMutation,
} from '../../../../graphql/graphql';
import { SelectValue } from '../../../../Components/Base/Select/Select';
import usePatientConsent from '../../../../Components/PatientProfile/Hooks/usePatientConsent';
import PatientNoConsentProvided from '../../../Patient/Components/PatientNoConsentProvided/PatientNoConsentProvided';
import Loading from '../../../../Components/Loading';
import CheckboxField from '../../../../Components/Form/components/CheckboxField';
import InputWrapper from '../../../../Components/Form/components/InputWrapper/InputWrapper';
import useNotification from '../../../../Hooks/useNotification.hook';
import useApiClient from '../../../Base/Hooks/useApiClient';

interface Props {
	patientId: string;
	targetPatientId: string;
	onSuccess: () => void;
	filesToShare: FileToShare[];
	isPatientsConsentRequired: boolean;
}

interface FileToTransfer {
	id: string;
	fileName: string;
	category: SelectValue;
	groupFiles: { id: string; add: boolean }[];
	saveAsPDF?: boolean;
}

const ECardMoveFileToHealthDocumentation: React.FC<Props> = ({
	onSuccess,
	targetPatientId,
	filesToShare,
	patientId,
	isPatientsConsentRequired,
}) => {
	const { t } = useTranslation();
	const { displayGQLError } = useApiClient();
	const { showNotificationSuccess } = useNotification();
	const [transferDocuments] = useTransferDocumentsMutation();
	const { isConsentProvided } = usePatientConsent(patientId);
	const [isLoading, setIsLoading] = useState(false);

	const validationSchema = yup.object({
		files: yup
			.array()
			.of(
				yup.object().shape({
					fileName: yup
						.string()
						.required(
							t('validation.eCardMoveFileToHealthDocumentation.fileName'),
						),
					category: yup.object().shape({
						value: yup
							.string()
							.uuid()
							.required(
								t('validation.eCardMoveFileToHealthDocumentation.category'),
							),
					}),
				}),
			)
			.required(),
	});

	const formMethods = useForm({
		resolver: yupResolver(validationSchema),
		defaultValues: {
			files: filesToShare.map(item => {
				return {
					id: item.id,
					fileName: item.name,
					fileId: item.id,
					groupFiles: item.file?.groupFiles?.map(groupFile => {
						return {
							id: groupFile?.id,
							add: true,
						};
					}),
				};
			}),
		},
	});

	const handleSave = async (values: any) => {
		setIsLoading(true);

		if (values?.files?.length) {
			const files = values.files as FileToTransfer[];

			let savedAsPDF = false;

			const filesToTransfer: DocumentTransferInput[] = [];

			files.forEach((item: FileToTransfer) => {
				const hasGroupedFiles = (item.groupFiles?.length || 0) > 0;
				const documentIds: string[] = hasGroupedFiles
					? item.groupFiles.filter(i => i.add).map(i => i.id)
					: [item.id];

				filesToTransfer.push({
					targetFolder: item.category.value as string,
					documentIds,
					grouped: documentIds.length > 1,
					documentName: item.fileName,
					targetUserId: targetPatientId,
					saveAsPDF: item.saveAsPDF,
				});

				if (item.saveAsPDF) {
					savedAsPDF = true;
				}
			});

			const response = await transferDocuments({
				input: filesToTransfer,
			});

			displayGQLError(response);

			if ('data' in response) {
				showNotificationSuccess({
					content: savedAsPDF
						? 'component.eCardMoveFileToHealthDocumentation.successPDFMessage'
						: 'component.eCardMoveFileToHealthDocumentation.successMessage',
				});
				onSuccess();
			}
		}

		setIsLoading(false);
	};

	if (!isConsentProvided && isPatientsConsentRequired) {
		return <PatientNoConsentProvided patientId={patientId} />;
	}

	if (isLoading) {
		return <Loading />;
	}

	return (
		<FormWrapper formMethods={formMethods} registerForChanges>
			<FormMultiplier name='files' allowAddForm={false}>
				{({ index }) => {
					const groupFiles = filesToShare[index]?.file?.groupFiles;
					const hasGroupedFiles = (groupFiles?.length || 0) > 0;
					return (
						<>
							<FormContainer settings={{ showLine: true }}>
								<div className='row'>
									<div className='col d-none'>
										<InputField
											name='id'
											label='fileId'
											className='mb-3'
											isRequired
										/>
									</div>
									<div className='col'>
										<InputField
											name='fileName'
											label='component.eCard.moveFileAs'
											className='mb-3'
											isRequired
										/>
									</div>
									<div className='col'>
										<ECardFolderSelectField
											patientId={targetPatientId}
											name='category'
											label='component.eCard.moveFileToFolder'
											className='mb-3'
											isRequired
										/>
									</div>
								</div>
								{hasGroupedFiles && (
									<div className='row'>
										<FormMultiplier
											name='groupFiles'
											allowAddForm={false}
											className={styles.groupFiles}>
											{item => {
												const thumbURL = groupFiles?.[item.index]?.thumbUrl;
												return (
													<div className={styles.file}>
														{thumbURL && <img src={thumbURL} />}
														<div className='d-none'>
															<InputField name='id' isRequired />
														</div>
														<CheckboxField
															name='add'
															className={styles.checkbox}
														/>
													</div>
												);
											}}
										</FormMultiplier>
										<div className='px-3'>
											<InputWrapper>
												<CheckboxField name='saveAsPDF' tx='base.saveAsPDF' />
											</InputWrapper>
										</div>
									</div>
								)}
							</FormContainer>
							<InputField name='fileId' className='d-none mb-3' isRequired />
						</>
					);
				}}
			</FormMultiplier>
			<div className={'mt-4'}>
				<SubmitButton
					title={'component.eCard.transfer'}
					onSubmit={handleSave}
				/>
			</div>
		</FormWrapper>
	);
};

export default ECardMoveFileToHealthDocumentation;
